import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * ATC desktop header menu controller.
 *
 * Parent menu items with submenus are click toggles; leaf items remain links.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item control.
 * @property {HTMLElement | null} overflowSubmenuItem - The expanded submenu control inside the overflow panel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    overflowSubmenuItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#handleDocumentClick, true);
    document.addEventListener('keydown', this.#handleKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#handleDocumentClick, true);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node) || !this.#state.activeItem) return;
    if (this.contains(event.target)) return;

    this.#deactivate();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    this.#deactivate();
  };

  /**
   * Get the overflow menu.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle a parent menu item open or closed.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    const submenu = findSubmenu(item);

    if (!item || !submenu) return;

    event.preventDefault();

    if (isOverflowItem(item)) {
      this.#toggleOverflowSubmenu(item);
      return;
    }

    if (item === this.#state.activeItem && item.getAttribute('aria-expanded') === 'true') {
      this.#deactivate();
      return;
    }

    if (this.#state.activeItem) {
      this.#deactivate(this.#state.activeItem);
    }

    this.activate(event);
  };

  /**
   * Toggle the overflow "More" panel without auto-opening the first overflowed item's submenu.
   * @param {MouseEvent} event
   */
  toggleOverflow = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    const listItem = event.target.closest('.menu-list__list-item');

    if (!item || listItem?.slot !== 'more') return;

    event.preventDefault();

    if (item === this.#state.activeItem && item.getAttribute('aria-expanded') === 'true') {
      this.#deactivate();
      return;
    }

    if (this.#state.activeItem) {
      this.#deactivate(this.#state.activeItem);
    }

    this.#activateOverflow(item);
  };

  /**
   * Activate the selected menu item immediately.
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = item.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.setAttribute('aria-expanded', 'false');
    }

    this.#state.activeItem = item;
    this.setAttribute('aria-expanded', 'true');
    item.setAttribute('aria-expanded', 'true');

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const opensHeaderPanel = Boolean(submenu) && (!isDefaultSlot || !isDropdownSubmenu(submenu));

    if (submenu) {
      this.dispatchEvent(new MegaMenuHoverEvent());
      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      if (opensHeaderPanel) {
        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                this.#cleanupMutationObserver();
              }
            });
          });
        });
        this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

        setTimeout(() => {
          this.#cleanupMutationObserver();
        }, 500);
      }
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerPanelHeight = opensHeaderPanel ? finalHeight : 0;
    const headerVisibleHeight = opensHeaderPanel ? this.#getHeaderVisibleHeight() : 0;

    this.headerComponent.style.setProperty('--submenu-height', `${headerPanelHeight}px`);
    this.#setFullOpenHeaderHeight(headerPanelHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Activate only the overflow panel.
   * @param {HTMLElement} item
   */
  #activateOverflow(item) {
    this.#collapseOverflowSubmenu();
    this.dataset.overflowExpanded = 'true';
    this.#state.activeItem = item;
    this.setAttribute('aria-expanded', 'true');
    item.setAttribute('aria-expanded', 'true');

    const overflowHeight = this.overflowMenu?.offsetHeight || 0;
    const overflowListHeight = this.#getOverflowListLinksHeight();
    const finalHeight = Math.max(overflowHeight, overflowListHeight);
    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Toggle a submenu inside the overflow panel without closing the parent "More" menu.
   * @param {HTMLElement} item
   */
  #toggleOverflowSubmenu(item) {
    const overflowTrigger = this.#getOverflowTrigger();
    const submenu = findSubmenu(item);

    if (!overflowTrigger || !submenu) return;

    if (this.#state.activeItem !== overflowTrigger) {
      if (this.#state.activeItem) {
        this.#deactivate(this.#state.activeItem);
      }

      this.#activateOverflow(overflowTrigger);
    }

    if (item === this.#state.overflowSubmenuItem && item.getAttribute('aria-expanded') === 'true') {
      this.#collapseOverflowSubmenu(item);
      this.#setOverflowHeight();
      return;
    }

    this.#collapseOverflowSubmenu();
    this.#state.overflowSubmenuItem = item;
    item.setAttribute('aria-expanded', 'true');
    submenu.dataset.active = '';
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#setOverflowHeight(submenu);
  }

  /**
   * Collapse a submenu inside the overflow panel.
   * @param {HTMLElement | null} [item]
   */
  #collapseOverflowSubmenu(item = this.#state.overflowSubmenuItem) {
    if (!item) return;

    const submenu = findSubmenu(item);

    item.setAttribute('aria-expanded', 'false');

    if (submenu) {
      delete submenu.dataset.active;
    }

    if (item === this.#state.overflowSubmenuItem) {
      this.#state.overflowSubmenuItem = null;
    }
  }

  /**
   * Recalculate the open height for the overflow panel.
   * @param {HTMLElement | null} [submenu]
   */
  #setOverflowHeight(submenu = null) {
    const overflowHeight = this.overflowMenu?.offsetHeight || 0;
    const overflowListHeight = this.#getOverflowListLinksHeight();
    const submenuHeight = submenu?.offsetHeight || 0;
    const finalHeight = Math.max(overflowHeight, overflowListHeight, submenuHeight);
    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active item after a pointer/focus event.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately.
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    const submenu = findSubmenu(item);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#collapseOverflowSubmenu();
    this.#state.activeItem = null;
    this.setAttribute('aria-expanded', 'false');
    item.setAttribute('aria-expanded', 'false');

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Get the "More" overflow trigger control.
   * @returns {HTMLElement | null}
   */
  #getOverflowTrigger() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.menu-list__list-item[slot="more"] [ref="menuitem"]'));
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item control.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  if (!listItem) return null;

  const menuitem = listItem.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Check if a menu item control has moved into the overflow panel.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isOverflowItem(element) {
  return element?.closest('.menu-list__list-item')?.slot === 'overflow';
}

/**
 * Check if a submenu should open as a compact dropdown instead of expanding the header panel.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isDropdownSubmenu(element) {
  return element instanceof HTMLElement && element.dataset.submenuType === 'dropdown';
}
